import { BulbTwoTone } from '@ant-design/icons';
import GGButton from 'components/buttons/GGButton';
import AppColors from 'config/AppColors';
import dayjs from 'dayjs';
import useSymptomTrackingData, { CycleStats } from 'hooks/useSymptomTrackingData';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  stubOverlayContainer: {
    position: 'relative',
  },
  stubOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(2px)',

    '& p': {
      margin: 0,
    },
  },
  stubTextContainer: {
    maxWidth: '80%',
    background: `radial-gradient(closest-side, rgba(255,255,255,1), rgba(255,255,255,0))`,
    // background: 'rgba(255,255,255,0.7)',
    // boxShadow: '0 0 16px 0 rgb(255, 255, 255, 1)',
    textAlign: 'center',
    padding: 24,
    borderRadius: 8,
  },
  summaryHeader: {
    display: 'flex',
    gap: 32,
    marginTop: 8,
    marginBottom: 24,
  },
  plusMinus: {
    fontSize: 17,
    color: AppColors.text.veryLightGray,
    fontWeight: 500,
  },
  summaryBlock: {
    color: AppColors.text.veryLightGray,
    fontSize: 14,
    '& strong': {
      display: 'flex',
      color: '#000',
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1,
      alignItems: 'center',
      gap: 4,
    },
  },
  periodHeader: {
    fontSize: 14,
    color: AppColors.text.veryLightGray,
    fontWeight: 500,
    margin: 0,
  },
  periodRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 12,
    fontSize: 16,
    width: 'fit-content',
    overflowX: 'hidden',
    maxWidth: '100%',
  },
  periodLabel: {
    width: '100%',
    fontWeight: 500,
    display: 'flex',
    justifyContent: 'space-between',
  },
  dots: {
    width: 'fit-content',
    display: 'flex',
    gap: 2,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: AppColors.text.veryLightGray,
    display: 'inline-block',
  },
  dotFilled: {
    background: AppColors.colorMap.veryBad,
  },
  periodInfo: {
    fontSize: 15,
    color: AppColors.text.veryLightGray,
    marginLeft: 8,
    fontWeight: 500,
  },
  currentInfo: {
    height: 20,
    lineHeight: '8px',
    background: AppColors.backgroundDarkGrey,
    color: '#fff',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 13,
    marginLeft: 8,
  },
  mainCallToActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 24,
  },
  disableExplanation: {
    fontSize: 14,
    color: AppColors.colorMap.bad,
    margin: 0,
    textAlign: 'center',
  },
});

const PastCyclesLength: React.FC<{ onFillCycles: () => void; onNoPeriods: () => void }> = ({
  onFillCycles,
  onNoPeriods,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [showPeriodDisabledExplainer, setShowPeriodDisabledExplainer] = useState(false);

  const { cycleStats: realCycleStats, cycleHistory: realHistory } = useSymptomTrackingData();

  const stubStats = {
    average: 28,
    maxVariationFromAverage: 1,
  };

  const stubHistory = [
    {
      start: dayjs().subtract(18, 'days'),
      totalLength: 18,
      periodLength: 5,
      completed: true,
    },
  ];

  const displayStub = realHistory.length < 2;

  const cycleStats = displayStub ? stubStats : realCycleStats;
  const cycleHistory = displayStub ? stubHistory : realHistory;

  const renderCycle = (cycle: CycleStats) => (
    <div className={styles.periodRow} key={cycle.start.format('DD/MM/YYYY')}>
      <div className={styles.periodLabel}>
        {cycle.start.format('LL')}

        {!cycle.completed ? (
          <span className={styles.currentInfo}>
            <Trans
              i18nKey="symptomTracking.cycles.inProgress"
              count={cycle.totalLength}
              components={{ sup: <sup /> }}
            />
          </span>
        ) : (
          <span className={styles.periodInfo}>
            {cycle.totalLength} {t('symptomTracking.nextPeriod.days')}
          </span>
        )}
      </div>
      <div className={styles.dots}>
        {Array.from({ length: cycle.totalLength }).map((_, i) => (
          <span key={i} className={i < cycle.periodLength ? `${styles.dot} ${styles.dotFilled}` : styles.dot} />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.stubOverlayContainer}>
        <div className={styles.summaryHeader}>
          <div className={styles.summaryBlock}>
            {t('symptomTracking.cycles.averageDuration')}
            <strong>
              <span>
                {Math.round(cycleStats.average)}{' '}
                <span style={{ fontSize: 16 }}>{t('symptomTracking.nextPeriod.days')}</span>
              </span>
            </strong>
          </div>
          <div className={styles.summaryBlock}>
            {t('symptomTracking.cycles.maxVariation')}
            <strong>
              <span className={styles.plusMinus}>±</span>
              <span>
                {Math.round(cycleStats.maxVariationFromAverage)}{' '}
                <span style={{ fontSize: 16 }}>{t('symptomTracking.nextPeriod.days')}</span>
              </span>
            </strong>
          </div>
        </div>
        <div>
          <h4 className={styles.periodHeader}>{t('symptomTracking.cycles.currentCycle')}</h4>
          {renderCycle(cycleHistory[cycleHistory.length - 1])}
          <h4 className={styles.periodHeader}>{t('symptomTracking.cycles.previousCycles')}</h4>
          {cycleHistory
            .slice(0, cycleHistory.length - 1)
            .reverse()
            .map(period => renderCycle(period))}
        </div>
        {displayStub && (
          <div className={styles.stubOverlay}>
            <div className={styles.stubTextContainer}>
              <p style={{ fontSize: 24 }}>
                <BulbTwoTone style={{ color: '#FFF5D6' }} twoToneColor="#FFD147" />
              </p>
              <p>{t('symptomTracking.history.fillAtLeast3Cycles')}</p>
            </div>
          </div>
        )}
      </div>
      {displayStub && (
        <div className={styles.mainCallToActionsContainer}>
          <GGButton block size="large" type="primary" onClick={onFillCycles}>
            {t('symptomTracking.history.fillCycles')}
          </GGButton>
          <GGButton
            block
            size="large"
            type="link"
            onClick={() => setShowPeriodDisabledExplainer(!showPeriodDisabledExplainer)}
          >
            {t('symptomTracking.cycles.noPeriods')}
          </GGButton>
          {showPeriodDisabledExplainer && (
            <>
              <p className={styles.disableExplanation}>
                <Trans i18nKey="symptomTracking.cycles.disableExplanation" components={{ br: <br /> }} />
              </p>
              <GGButton block size="large" type="link" onClick={onNoPeriods}>
                {t('symptomTracking.cycles.disable')}
              </GGButton>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PastCyclesLength;
